import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Check, AlertCircle } from 'lucide-react';
import { pushNotificationService } from '../../services/pushNotificationService';
import { toast } from 'react-hot-toast';

interface PushNotificationSettingsProps {
  showInline?: boolean;
}

const PushNotificationSettings: React.FC<PushNotificationSettingsProps> = ({ showInline = false }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    const enabled = await pushNotificationService.isSubscribed();
    const perm = pushNotificationService.getPermissionStatus();
    setIsEnabled(enabled);
    setPermission(perm);
  };

  const handleToggle = async () => {
    if (loading) return;

    setLoading(true);

    try {
      if (isEnabled) {
        // Unsubscribe
        const success = await pushNotificationService.unsubscribeFromPushNotifications();
        if (success) {
          setIsEnabled(false);
          toast.success('Push notifications disabled');
        } else {
          toast.error('Failed to disable push notifications');
        }
      } else {
        // Subscribe
        const success = await pushNotificationService.subscribeToPushNotifications();
        if (success) {
          setIsEnabled(true);
          setPermission('granted');
          toast.success('Push notifications enabled! 🔔');
          
          // Send test notification
          setTimeout(() => {
            pushNotificationService.sendTestNotification();
          }, 1000);
        } else {
          toast.error('Failed to enable push notifications. Please check permissions.');
        }
      }
    } catch (error) {
      console.error('Error toggling push notifications:', error);
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (showInline) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`rounded-full p-2 ${isEnabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
              {isEnabled ? (
                <Bell className="h-5 w-5 text-blue-600" />
              ) : (
                <BellOff className="h-5 w-5 text-gray-600" />
              )}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Push Notifications
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Get notified when your AI consultant has updates or insights
              </p>
              
              {permission === 'denied' && (
                <div className="mt-2 flex items-center gap-2 rounded-lg bg-orange-50 px-3 py-2">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  <p className="text-xs text-orange-800">
                    Notifications blocked. Enable in browser settings.
                  </p>
                </div>
              )}

              {isEnabled && (
                <div className="mt-2 flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2">
                  <Check className="h-4 w-4 text-green-600" />
                  <p className="text-xs text-green-800">
                    You'll receive notifications about chat updates
                  </p>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleToggle}
            disabled={loading || permission === 'denied'}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
              isEnabled ? 'bg-blue-600' : 'bg-gray-200'
            } ${loading || permission === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                isEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>
    );
  }

  // Card layout for settings page
  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`rounded-full p-3 ${isEnabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
            {isEnabled ? (
              <Bell className="h-6 w-6 text-blue-600" />
            ) : (
              <BellOff className="h-6 w-6 text-gray-600" />
            )}
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Push Notifications
            </h2>
            <p className="text-sm text-gray-600">
              Stay updated with real-time alerts
            </p>
          </div>
        </div>

        <button
          onClick={handleToggle}
          disabled={loading || permission === 'denied'}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
            isEnabled ? 'bg-blue-600' : 'bg-gray-200'
          } ${loading || permission === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              isEnabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      <div className="space-y-3">
        {permission === 'denied' && (
          <div className="rounded-lg bg-orange-50 p-4 border border-orange-200">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-orange-800">
                  Notifications are blocked
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  To enable notifications, please update your browser settings and reload this page.
                </p>
              </div>
            </div>
          </div>
        )}

        {permission === 'default' && !isEnabled && (
          <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
            <div className="flex gap-3">
              <Bell className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Enable push notifications
                </p>
                <p className="text-sm text-blue-800 mt-1">
                  Get instant updates about your AI consultant responses, business insights, and important reminders.
                </p>
              </div>
            </div>
          </div>
        )}

        {isEnabled && (
          <div className="rounded-lg bg-green-50 p-4 border border-green-200">
            <div className="flex gap-3">
              <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900">
                  Notifications enabled
                </p>
                <p className="text-sm text-green-800 mt-1">
                  You'll receive notifications about:
                </p>
                <ul className="text-sm text-green-800 mt-2 ml-4 space-y-1 list-disc">
                  <li>AI consultant responses</li>
                  <li>Business plan updates</li>
                  <li>CRM activity reminders</li>
                  <li>Important system notifications</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={() => pushNotificationService.sendTestNotification()}
          disabled={!isEnabled}
          className="w-full rounded-lg border-2 border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          Send Test Notification
        </button>
      </div>
    </div>
  );
};

export default PushNotificationSettings;
